'use client';

import React, { useState, useMemo } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { motion, AnimatePresence, useReducedMotion, Variants } from 'framer-motion';
import { Heading, Text, Button, Badge } from '@/components/ui/design-system';
import { ArrowRight } from 'lucide-react';

// Define the structure of a product category
export interface Category {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  image_url: string | null;
  product_groups: {
    slug: string;
  };
}

interface ToggleOption {
  value: string;
  label: string;
  icon: React.ReactNode;
}

interface ProductToggleClientProps {
  toggleOptions: ToggleOption[];
  categories: Category[];
  id?: string;
}

// Animation variants for container elements to orchestrate staggered animations
const containerVariants: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

// Animation variants for individual items entering the view
const itemVariants: Variants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      type: 'spring' as const,
      stiffness: 100,
      damping: 15,
    },
  },
};

export function ProductToggleClient({ toggleOptions, categories, id }: ProductToggleClientProps) {
  const [selectedGroup, setSelectedGroup] = useState(toggleOptions[0]?.value || '');
  const shouldReduceMotion = useReducedMotion();

  // Memoize the filtered categories to avoid re-calculation on every render
  const filteredCategories = useMemo(() => {
    if (!selectedGroup || !categories || categories.length === 0) return [];
    return categories.filter(c => c.product_groups.slug === selectedGroup);
  }, [selectedGroup, categories]);

  // Determine grid layout based on selected group
  const gridClasses = useMemo(() => {
    // First group: 2 columns on desktop
    if (selectedGroup === toggleOptions[0]?.value) {
      return 'grid-cols-1 lg:grid-cols-2';
    }
    // Second group: 3 columns on desktop
    if (selectedGroup === toggleOptions[1]?.value) {
      return 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3';
    }
    // Fallback for any other cases
    return 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3';
  }, [selectedGroup, toggleOptions]);

  return (
    <section id={id} className="py-16 md:py-24 bg-white dark:bg-neutral-950">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.5 }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
          className="flex flex-col items-center text-center mb-12"
        >
          <Badge variant="premium" size="lg" className="mb-4">
            Our Core Offerings
          </Badge>
          <Heading level={2} className="mb-4">
            Your One-Stop Shop for Packaging Solutions
          </Heading>
          <Text className="mt-4 text-lg text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
            From recyclable PET jars, bottles, and cans to preforms and custom accessories — we offer a complete range of eco-conscious packaging solutions. With a focus on custom designs and sustainable sourcing, we simplify your packaging needs, all in one place.
          </Text>
        </motion.div>

        {/* Toggle Buttons */}
        <div className="flex justify-center mb-12">
          <motion.div
            role="tablist"
            aria-orientation="horizontal"
            className="bg-gray-100 dark:bg-gray-800 rounded-full p-1.5 flex items-center space-x-2"
            variants={shouldReduceMotion ? undefined : containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.8 }}
          >
            {toggleOptions.map(option => (
              <motion.div key={option.value} variants={itemVariants} className="flex">
                <button
                  role="tab"
                  aria-selected={selectedGroup === option.value}
                  aria-controls={`product-panel-${option.value}`}
                  id={`product-tab-${option.value}`}
                  onClick={() => setSelectedGroup(option.value)}
                  className={`relative px-4 py-2.5 md:px-6 md:py-3 text-sm md:text-base font-semibold transition-colors duration-300 ease-in-out flex items-center rounded-full w-full`}
                >
                  {selectedGroup === option.value && (
                    <motion.div
                      layoutId="active-toggle-highlight"
                      className="absolute inset-0 bg-white dark:bg-gray-900 rounded-full shadow-md"
                      transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                    />
                  )}
                  <span className="relative z-10 flex items-center">
                    {option.icon}
                    <span className={`${selectedGroup === option.value ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400'}`}>
                      {option.label}
                    </span>
                  </span>
                </button>
              </motion.div>
            ))}
          </motion.div>
        </div>

        {/* Category Grid */}
        <AnimatePresence mode="wait">
          <motion.div
            key={selectedGroup} // Re-trigger animation when the selected group changes
            role="tabpanel"
            id={`product-panel-${selectedGroup}`}
            aria-labelledby={`product-tab-${selectedGroup}`}
            initial="hidden"
            animate="visible"
            exit={{ opacity: 0, y: -20 }}
            variants={shouldReduceMotion ? undefined : containerVariants}
            transition={{ duration: 0.4, ease: 'easeInOut' }}
          >
            {filteredCategories.length > 0 ? (
              <motion.div className={`grid ${gridClasses} gap-4 md:gap-5`} variants={shouldReduceMotion ? undefined : containerVariants}>
                {filteredCategories.map(category => (
                  <motion.div key={category.id} variants={itemVariants}>
                    <Link href={`/products/${category.slug}`} passHref>
                      <motion.div
                        whileHover={shouldReduceMotion ? {} : { scale: 1.03, y: -5, boxShadow: '0px 10px 30px -5px rgba(0, 0, 0, 0.3)' }}
                        transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                        className="group relative block h-80 w-full overflow-hidden rounded-xl shadow-lg"
                      >
                        <Image
                          src={category.image_url || '/placeholder.jpg'}
                          alt={category.name}
                          fill
                          sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
                          className="object-cover transition-transform duration-300 ease-in-out group-hover:scale-105"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/40 to-transparent" />
                        <div className="absolute inset-0 flex flex-col justify-end p-6 text-white overflow-hidden">
                          <div className="transition-transform duration-300 ease-in-out group-hover:-translate-y-16">
                            <Heading level={2} className="font-bold text-white">
                              {category.name}
                            </Heading>
                            <Text className="mt-1 text-white/90 line-clamp-2">
                              {category.description}
                            </Text>
                          </div>
                          <div className="absolute bottom-6 left-6 right-6 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                            <Button variant="primary" size="md" className="pointer-events-none w-full sm:w-auto">
                              Learn More
                              <ArrowRight className="ml-2 h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </motion.div>
                    </Link>
                  </motion.div>
                ))}
              </motion.div>
            ) : (
              <div className="text-center py-16">
                <Heading level={3} className="text-gray-700 dark:text-gray-300">
                  Coming Soon!
                </Heading>
                <Text className="mt-4 text-gray-500 dark:text-gray-400">
                  We're working on adding new products to this category. Please check back later.
                </Text>
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
}
